import type {
  LegalEntity,
  RegisterMutation,
  ShareType,
  ShareTypeInput,
  ShareTypeVersion,
} from 'generated/gql';
import { MutationType, useAvailableShareTypesForCertificationQuery } from 'generated/gql';
import { Divider, Text, TileGroup } from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { Column, Columns } from 'components/Columns';
import { FakeInput } from 'components/FakeInput/FakeInput';
import { InputField } from 'components/FormFields/InputField';
import { NumberField } from 'components/FormFields/NumberField';
import { SelectField } from 'components/FormFields/SelectField';
import { booleanOptions, booleanTransform } from 'forms/helpers/booleanOptions';
import currencyCodeOptions from 'forms/helpers/currencyCodeOptions';
import { formatMutationType, mutationLabelObject } from 'helpers/formatMutation';
import { formatDate, formatShareTypeName } from 'helpers/formatShares';
import {
  defaultCurrencyCode,
  formatMoney,
  moneyToNumber,
  multiplyMoney,
  numberToMoney,
} from 'helpers/money';
import { certificateTypeOptions, shareTypeOptions } from 'helpers/shareTypes';
import { omit, pick } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { DeepPartial } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import styled from 'styled-components';

export const getShareTypeEditFormSchema = (isFoundation = false) => {
  return yup.object({}).shape(
    {
      type: yup.string().trim().nullable(),
      classifier: yup.string().trim().nullable(),
      underlyingShareTypeId: isFoundation
        ? yup.string().trim().required()
        : yup.string().trim().nullable(),
      certificatesPerShare: isFoundation
        ? yup.number().moreThan(0).required().default(1)
        : yup.number().nullable(),
      voting: yup
        .boolean()
        .when('dividend', {
          is: false,
          then: yup.boolean().oneOf([true], () => t`Stem- en/of dividendrecht is vereist`),
        })
        .default(true),
      dividend: yup
        .boolean()
        .when('voting', {
          is: false,
          then: yup.boolean().oneOf([true], () => ''),
        })
        .default(true),
      meeting: yup.boolean().nullable().default(false),
      currencyCode: yup.string().trim().required().default(defaultCurrencyCode),
      name: yup.string().trim().required(),
      nominalValue: yup.number().moreThan(0).required(),
      hasAuthorizedCapital: yup.boolean().default(false),
      authorizedCapitalAmount: yup
        .mixed()
        .when('hasAuthorizedCapital', {
          is: true,
          then: yup.number().integer().min(1).required(),
          otherwise: yup.number().nullable().optional(),
        })
        .default(0),
      createdInMutationId: yup.string().trim().required(),
    },
    ['voting', 'dividend'] as any,
  );
};

export const shareTypeEditFormSchema = getShareTypeEditFormSchema();

export type ShareTypeEditFormValues = yup.TypeOf<typeof shareTypeEditFormSchema>;

const UnderlyingShares = styled.fieldset`
  margin: unset;
  border: 1px solid ${({ theme }) => theme.designTokens.colors.grey30};

  legend {
    color: ${({ theme }) => theme.designTokens.colors.grey90};
  }
`;

export enum ShareTypeFormMode {
  Create = 'CREATE',
  Correct = 'CORRECT',
  Update = 'UPDATE',
}

export interface ShareTypeFormProps {
  isFoundation: boolean;
  nestedField?: string;
  mutations?: Array<Pick<RegisterMutation, 'id' | 'type' | 'title' | 'effectiveDate'>>;
  canEditCreatedInMutation: boolean;
  stakId?: string;
  currentUnderlyingShareType?:
    | (Pick<ShareType, 'id' | 'classifier' | 'name' | 'nominalValue'> & {
        register: { legalEntity: Pick<LegalEntity, 'name'> };
      })
    | null;
}

const buildUnderlyingShareTypeOption = (
  legalEntityName: string,
  shareType: Pick<ShareType, 'id' | 'classifier' | 'name' | 'nominalValue'>,
) => ({
  label: `${legalEntityName} — ${formatShareTypeName(shareType, false)}`,
  value: shareType.id,
});

const ShareTypeEditForm = ({
  isFoundation,
  nestedField = '',
  mutations = [],
  canEditCreatedInMutation = false,
  stakId,
  currentUnderlyingShareType,
}: ShareTypeFormProps) => {
  const { control, trigger, reset, getValues, setValue } = useFormContext();
  const hasAuthorizedCapital = useWatch({ control, name: `${nestedField}hasAuthorizedCapital` });
  const authorizedCapitalAmount = useWatch({
    control,
    name: `${nestedField}authorizedCapitalAmount`,
  });
  const [nominalValue, currencyCode, voting, dividend, name, type] = useWatch({
    control,
    name: [
      `${nestedField}nominalValue`,
      `${nestedField}currencyCode`,
      `${nestedField}voting`,
      `${nestedField}dividend`,
      `${nestedField}name`,
      `${nestedField}type`,
    ],
  });

  const authorizedCapital = useMemo(() => {
    if (authorizedCapitalAmount && nominalValue) {
      return formatMoney(
        multiplyMoney(
          numberToMoney({ value: nominalValue, currencyCode }),
          authorizedCapitalAmount,
        ),
      );
    }
  }, [authorizedCapitalAmount, nominalValue, currencyCode]);

  const mutationOptions = useMemo(
    () =>
      mutations
        .filter(
          (mutation) =>
            mutationLabelObject[mutation.type].canChangeAoA ||
            mutation.type === MutationType.Onboarding,
        )
        .map((mutation) => ({
          label: `${formatDate(mutation.effectiveDate)} (${formatMutationType(mutation)})`,
          value: mutation.id,
        })),
    [mutations],
  );

  const options = useMemo(() => {
    return isFoundation ? certificateTypeOptions() : shareTypeOptions();
  }, [isFoundation]);

  const { data: certificationData, loading: certificationLoading } =
    useAvailableShareTypesForCertificationQuery({
      variables: { stakId: stakId as string },
      skip: !isFoundation || !stakId,
    });

  const underlyingShareTypeOptions = useMemo(() => {
    const fetchedOptions = (certificationData?.availableShareTypesForCertification ?? []).map(
      ({ legalEntity, shareType }) => buildUnderlyingShareTypeOption(legalEntity.name, shareType),
    );
    if (
      !currentUnderlyingShareType ||
      fetchedOptions.some((option) => option.value === currentUnderlyingShareType.id)
    ) {
      return fetchedOptions;
    }
    return [
      buildUnderlyingShareTypeOption(
        currentUnderlyingShareType.register.legalEntity.name,
        currentUnderlyingShareType,
      ),
      ...fetchedOptions,
    ];
  }, [certificationData, currentUnderlyingShareType]);

  // When exactly one underlying share type is available it is shown as a
  // read-only value instead of a selectable dropdown.
  const singleUnderlyingShareType = useMemo(
    () =>
      !certificationLoading && underlyingShareTypeOptions.length === 1
        ? underlyingShareTypeOptions[0]
        : undefined,
    [certificationLoading, underlyingShareTypeOptions],
  );

  const underlyingShareTypeId = useWatch({ control, name: `${nestedField}underlyingShareTypeId` });

  const handleTypeChange = useCallback(
    (value?: string) => {
      const option = options.find((option) => option.value === value);
      if (!option) return;
      const names = options.map((option) => option.name);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { label: __label, value: __value, name: optionName, ...shareTypeFields } = option;
      const resetValues: DeepPartial<ShareType> = {
        ...shareTypeFields,
      };
      if (!name || names.includes(name)) resetValues.name = optionName;
      reset({ ...getValues(), ...resetValues }, { keepDirty: true });
    },
    [name, getValues, options, reset],
  );

  useEffect(() => {
    trigger(`${nestedField}dividend`);
  }, [voting, nestedField, trigger]);
  useEffect(() => {
    trigger(`${nestedField}voting`);
  }, [dividend, nestedField, trigger]);

  // Pre-fill the nominal value with the selected underlying share type's nominal
  // value whenever the selection changes. The user is free to override it afterwards.
  const previousUnderlyingShareTypeId = useRef(underlyingShareTypeId);
  useEffect(() => {
    if (underlyingShareTypeId === previousUnderlyingShareTypeId.current) return;
    previousUnderlyingShareTypeId.current = underlyingShareTypeId;
    const fetched = certificationData?.availableShareTypesForCertification.find(
      ({ shareType }) => shareType.id === underlyingShareTypeId,
    );
    const nominalValue = fetched
      ? fetched.shareType.nominalValue
      : currentUnderlyingShareType?.id === underlyingShareTypeId
        ? currentUnderlyingShareType?.nominalValue
        : null;
    if (!nominalValue) return;
    setValue(`${nestedField}nominalValue`, moneyToNumber(nominalValue), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [underlyingShareTypeId, certificationData, currentUnderlyingShareType, nestedField, setValue]);

  // The read-only single-option case does not render the dropdown that would
  // otherwise auto-select the only value, so set it here to keep the form valid
  // and trigger the nominal-value pre-fill above.
  useEffect(() => {
    if (!singleUnderlyingShareType) return;
    if (underlyingShareTypeId === singleUnderlyingShareType.value) return;
    setValue(`${nestedField}underlyingShareTypeId`, singleUnderlyingShareType.value, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [singleUnderlyingShareType, underlyingShareTypeId, nestedField, setValue]);

  return (
    <>
      {!!options.length && (
        <TileGroup
          defaultValue={type}
          label={t`Type`}
          name="type"
          options={options.map(({ label, value }) => ({ label, value }))}
          spaceAfter={10}
          variant="horizontal-stretch"
          onChange={handleTypeChange}
        />
      )}

      {isFoundation ? (
        <Columns count={2} spaceAfter={0}>
          <Column>
            <SelectField
              label={t`Dividendrecht`}
              name={`${nestedField}dividend`}
              options={booleanOptions()}
              placeholder={t`Dividendrecht`}
              readOnly={!isFoundation}
              transform={booleanTransform}
            />
          </Column>

          <Column>
            <SelectField
              label={t`Vergaderrecht`}
              name={`${nestedField}meeting`}
              options={booleanOptions()}
              placeholder={t`Vergaderrecht`}
              transform={booleanTransform}
            />
          </Column>
        </Columns>
      ) : (
        <Columns count={2} spaceAfter={6}>
          <Column>
            <FakeInput label={t`Stemrecht`} value={voting ? t`Ja` : t`Nee`} />
          </Column>

          <Column>
            <FakeInput label={t`Dividendrecht`} value={dividend ? t`Ja` : t`Nee`} />
          </Column>
        </Columns>
      )}

      <Divider spaceAfter={6} />
      <Columns count={2}>
        <Column>
          <SelectField
            required
            label={t`Valuta`}
            name={`${nestedField}currencyCode`}
            options={currencyCodeOptions()}
            placeholder={t`Valuta`}
          />
        </Column>
        <Column>
          <NumberField
            required
            label={t`Nominale waarde`}
            maxDecimals={9}
            name={`${nestedField}nominalValue`}
            placeholder={t`Nominale waarde`}
          />
        </Column>
      </Columns>

      <Columns count={2}>
        <Column>
          <InputField
            label={t`Letter`}
            name={`${nestedField}classifier`}
            placeholder={t`Letter`}
            spaceAfter={0}
          />
        </Column>
        <Column>
          <InputField
            required
            label={t`Benaming`}
            name={`${nestedField}name`}
            placeholder={t`Benaming`}
            spaceAfter={0}
          />
        </Column>
      </Columns>

      {isFoundation ? (
        <>
          <Divider spaceAfter={6} spaceBefore={6} />

          <UnderlyingShares>
            <legend>
              <Trans>Underlying shares</Trans>
            </legend>

            {underlyingShareTypeOptions.length === 0 ? (
              <Text color="grey80" type="paragraph">
                <Trans>No shares found to certify.</Trans>
              </Text>
            ) : (
              <Columns count={2}>
                <Column>
                  {singleUnderlyingShareType ? (
                    <FakeInput label={t`Share type`} value={singleUnderlyingShareType.label} />
                  ) : (
                    <SelectField
                      required
                      label={t`Share type`}
                      name={`${nestedField}underlyingShareTypeId`}
                      options={underlyingShareTypeOptions}
                      placeholder={t`Select company and share type`}
                      spaceAfter={0}
                    />
                  )}
                </Column>
                <Column>
                  <NumberField
                    required
                    label={t`Certificates per share`}
                    name={`${nestedField}certificatesPerShare`}
                    placeholder={t`Certificates per share`}
                    spaceAfter={0}
                  />
                </Column>
              </Columns>
            )}
          </UnderlyingShares>
        </>
      ) : (
        <>
          <Divider spaceAfter={6} spaceBefore={6} />

          <Columns count={2}>
            <Column>
              <SelectField
                label={t`Maatschappelijk kapitaal`}
                name={`${nestedField}hasAuthorizedCapital`}
                options={booleanOptions()}
                spaceAfter={0}
                transform={booleanTransform}
              />
            </Column>
            <Column>
              {hasAuthorizedCapital && (
                <>
                  <NumberField
                    required
                    label={t`Aantal aandelen`}
                    name={`${nestedField}authorizedCapitalAmount`}
                    placeholder={t`Aantal aandelen`}
                    spaceAfter={0}
                  />
                  {authorizedCapital && (
                    <Text color="grey80" type="labelTiny">
                      <Trans>Maatschappelijk kapitaal</Trans>: {authorizedCapital}
                    </Text>
                  )}
                </>
              )}
            </Column>
          </Columns>
        </>
      )}

      {canEditCreatedInMutation && (
        <>
          <Divider spaceAfter={6} spaceBefore={6} />

          <Columns count={2}>
            <Column>
              <SelectField
                label={t`Aangemaakt in mutatie`}
                name={`${nestedField}createdInMutationId`}
                options={mutationOptions}
                popoverPlacement="auto-start"
                spaceAfter={0}
              />
            </Column>
          </Columns>
        </>
      )}
    </>
  );
};

export const mapShareTypeVersionToShareTypeEditFormValues = (
  shareTypeVersion: ShareTypeVersion,
  mutationId?: string,
): ShareTypeEditFormValues =>
  ({
    ...pick(shareTypeVersion, [
      'classifier',
      'dividend',
      'meeting',
      'name',
      'voting',
      'authorizedCapitalAmount',
      'certificatesPerShare',
    ]),
    nominalValue: moneyToNumber(shareTypeVersion.nominalValue),
    currencyCode: shareTypeVersion.nominalValue.currencyCode,
    underlyingShareTypeId: shareTypeVersion.underlyingShareType?.id,
    createdInMutationId: mutationId || shareTypeVersion.createdInMutation?.id || null,
    hasAuthorizedCapital: !!shareTypeVersion.authorizedCapitalAmount,
    type: shareTypeOptions().find(
      (option) =>
        option.voting === shareTypeVersion.voting && option.dividend === shareTypeVersion.dividend,
    )?.value,
  }) as ShareTypeEditFormValues;

export const mapShareTypeEditFormValuesToShareTypeInput = ({
  nominalValue,
  currencyCode,
  hasAuthorizedCapital,
  authorizedCapitalAmount,
  ...formValues
}: ShareTypeEditFormValues): ShareTypeInput =>
  ({
    ...omit(formValues, ['type', 'hasAuthorizedCapital']),
    nominalValue: numberToMoney({ value: nominalValue, currencyCode } as any),
    authorizedCapitalAmount: hasAuthorizedCapital ? authorizedCapitalAmount : 0,
  }) as ShareTypeInput;

export { ShareTypeEditForm, shareTypeEditFormSchema as ShareTypeEditFormSchema };
